import { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import PageContainer from 'components/PageContainer';
import { Button, CircularProgress } from '@mui/material';
import { EXITS } from 'adminConstants/routes';
import Summary from '../Common/Summary';
import useExitStatement from 'hooks/data/exit/useExitStatement';
import useStyles from '../Common/styles';
import UpdateExitStatementDetails, { FormData } from './UpdateDetail';
import PageContent from 'components/PageContent';
import RecordView from 'components/RecordView';
import ButtonGroup from 'components/ButtonGroup';
import api from 'lib/trpcClient';

type Params = {
  id: string;
  exitAmendmentId?: string;
};

const UpdateExitStatement = () => {
  const history = useHistory();
  const { classes } = useStyles();
  const heading = 'Investor communications: Exit statement';
  const breadcrumbs = [
    { label: 'Dashboard', link: '/' },
    { label: ' Exit statement', link: '/', isActive: true },
  ];

  const { id } = useParams<Params>();

  const steps = { updateExitStatementDetails: 1, summary: 2 };
  const [activeStep, setActiveStep] = useState(
    steps.updateExitStatementDetails,
  );

  const {
    exitStatement: { data, isLoading },
    updateExitStatement,
  } = useExitStatement({ id });

  const exitId = data?.exitStatement?.exitId?.toString();

  const previousExitStatementForCompanyLegalNameExists =
    api.exitStatement.checkPreviousExitStatementForCompanyLegalName.useQuery(
      {
        exitId: exitId ?? '',
      },
      {
        enabled: !!exitId,
        retry: false,
      },
    );

  const statement = data?.exitStatement;

  const [formValues, setFormValues] = useState<
    FormData & {
      notifyInvestors?: boolean;
      includePreviousExitStatements?: boolean;
    }
  >({
    statementDate: new Date(),
    title: '',
    summary: '',
    letter: '',
    notes: '',
    supportingFiles: [],
    notifyInvestors: false,
    includePreviousExitStatements: false,
  });

  useEffect(() => {
    if (statement?.submitDate) {
      setFormValues((prev) => ({
        ...prev,
        statementDate: new Date(statement.submitDate),
      }));
    }
  }, [statement?.submitDate]);

  useEffect(() => {
    if (statement?.includePreviousExitStatements !== undefined) {
      setFormValues((prev) => ({
        ...prev,
        includePreviousExitStatements: statement.includePreviousExitStatements,
      }));
    }
  }, [statement?.includePreviousExitStatements]);

  const handleNext = () => {
    setActiveStep((currentStep) => currentStep + 1);
  };

  const handleSubmitDetails = (values: FormData) => {
    // Preserve notifyInvestors and includePreviousExitStatements across steps
    setFormValues((prev) => ({
      ...prev,
      ...values,
    }));
    handleNext();
  };

  const handleSubmit = () => {
    if (!formValues || !id || !formValues.statementDate) return;

    updateExitStatement.mutate({
      exitStatementId: id,
      data: {
        title: formValues.title,
        summary: formValues.summary,
        letter: formValues.letter,
        notes: formValues.notes,
        supportingFiles: formValues.supportingFiles,
        notifyInvestors: formValues.notifyInvestors || false,
        statementDate: formValues.statementDate,
        includePreviousExitStatements:
          formValues.includePreviousExitStatements || false,
      },
    });
  };

  useEffect(() => {
    if (updateExitStatement.isSuccess) {
      history.push(EXITS);
    }
  }, [updateExitStatement.isSuccess]);

  if (isLoading) {
    return <CircularProgress size={20} />;
  }

  const pageTitle =
    activeStep === steps.updateExitStatementDetails
      ? 'Exit statement details'
      : 'Exit statement summary';

  return (
    <PageContainer heading={heading} breadcrumbs={breadcrumbs}>
      <PageContent header={pageTitle}>
        <RecordView>
          {steps.updateExitStatementDetails === activeStep ? (
            <UpdateExitStatementDetails
              onNext={handleSubmitDetails}
              defaultValues={{
                statementDate: statement?.submitDate
                  ? new Date(statement.submitDate)
                  : new Date(),
                title: statement?.title || '',
                summary: statement?.summary || '',
                letter: statement?.letter || '',
                notes: statement?.notes || '',
                supportingFiles: statement?.supportingFiles || [],
              }}
              exitId={statement?.exitId?.toString()}
              exitAmendmentId={statement?.exitAmendmentId?.toString()}
              notifyInvestors={formValues.notifyInvestors}
              setNotifyInvestors={(value) =>
                setFormValues({ ...formValues, notifyInvestors: value })
              }
              previousExitStatementForCompanyLegalNameExists={
                previousExitStatementForCompanyLegalNameExists.data ?? false
              }
              includePreviousExitStatementsValue={
                formValues.includePreviousExitStatements
              }
              setIncludePreviousExitStatements={(value) =>
                setFormValues({
                  ...formValues,
                  includePreviousExitStatements: value,
                })
              }
              tradingName={statement?.companies?.[0]?.tradingName ?? ''}
            />
          ) : (
            <Summary
              statementDate={formValues.statementDate}
              title={formValues.title}
              summary={formValues.summary}
              letter={formValues.letter}
              notes={formValues.notes}
              supportingFiles={formValues.supportingFiles}
            />
          )}

          {activeStep === steps.summary && (
            <ButtonGroup>
              <Button
                variant="outlined"
                onClick={() => {
                  setActiveStep(steps.updateExitStatementDetails);
                }}
              >
                Back
              </Button>

              <Button
                className={classes.buttonLeftMargin}
                color="primary"
                variant="contained"
                disabled={updateExitStatement.isPending}
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </ButtonGroup>
          )}
        </RecordView>
      </PageContent>
    </PageContainer>
  );
};
export default UpdateExitStatement;
